import { useState, useEffect, useCallback, useMemo } from 'react'
import axios from 'axios'
import dayjs from 'dayjs'
import { LicenseData } from './types'
import { getParentOrigin, getParentUrlParam } from './helpers'

const parentOrigin = getParentOrigin()

export interface LicenseFormState {
  name: string
  type: string
  contactName: string
  contactEmail: string
  startsAt: string
  endsAt: string
  districtName: string
  schoolName: string
  seatLimit: string
}

const defaultForm: LicenseFormState = {
  name: '',
  type: '',
  contactName: '',
  contactEmail: '',
  startsAt: '',
  endsAt: '',
  districtName: '',
  schoolName: '',
  seatLimit: '',
}

function formFromLicense(lic: LicenseData['license']): LicenseFormState {
  return {
    name: lic.name,
    type: lic.licenseType,
    contactName: lic.contactName,
    contactEmail: lic.contactEmail,
    startsAt: lic.startsAt ? dayjs(lic.startsAt).format('YYYY-MM-DD') : '',
    endsAt: lic.endsAt ? dayjs(lic.endsAt).format('YYYY-MM-DD') : '',
    districtName: lic.districtName,
    schoolName: lic.schoolName,
    seatLimit: lic.seatLimit != null ? String(lic.seatLimit) : '',
  }
}

export function useLicense() {
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMsg, setSuccessMsg] = useState<string | null>(null)
  const [data, setData] = useState<LicenseData | null>(null)
  const [lCode, setLCode] = useState('')
  const [form, setForm] = useState<LicenseFormState>(defaultForm)

  const fetchLicense = useCallback(async (code: string, silent = false) => {
    if (!silent) setLoading(true)
    try {
      const response = await axios.get(`${parentOrigin}/api/license`, {
        params: { l: code },
      })
      const licenseData = response.data as LicenseData
      setData(licenseData)
      setForm(formFromLicense(licenseData.license))
    } catch (err: unknown) {
      const errorMessage =
        (err as { response?: { data?: string } }).response?.data ||
        (err as { message?: string }).message ||
        'Failed to fetch license data'
      setError(errorMessage)
    } finally {
      if (!silent) setLoading(false)
    }
  }, [])

  const postAction = useCallback(
    async (action: string, extraBody: Record<string, unknown> = {}) => {
      setActionLoading(true)
      setError(null)
      setSuccessMsg(null)
      try {
        const response = await axios.post(
          `${parentOrigin}/api/license`,
          { action, code: lCode, ...extraBody },
          { params: { l: lCode } }
        )
        setSuccessMsg(response.data?.message || 'Action completed successfully')
        await fetchLicense(lCode, true)
        try {
          window.parent.scrollTo({ top: 0, behavior: 'smooth' })
        } catch {
          window.scrollTo({ top: 0, behavior: 'smooth' })
        }
      } catch (err: unknown) {
        const msg =
          (err as { response?: { data?: { error?: string } } }).response?.data
            ?.error ||
          (err as { response?: { data?: string } }).response?.data ||
          (err as { message?: string }).message ||
          'Action failed'
        setError(typeof msg === 'string' ? msg : 'Action failed')
        throw err
      } finally {
        setActionLoading(false)
      }
    },
    [lCode, fetchLicense]
  )

  const resetForm = useCallback(() => {
    if (data) setForm(formFromLicense(data.license))
  }, [data])

  const initialForm = useMemo(
    () => (data ? formFromLicense(data.license) : null),
    [data]
  )
  const formHasChanges = useMemo(() => {
    if (!initialForm) return false
    return (Object.keys(initialForm) as (keyof LicenseFormState)[]).some(
      (k) => form[k] !== initialForm[k]
    )
  }, [form, initialForm])

  const updateFormField = useCallback((field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }))
  }, [])

  useEffect(() => {
    const code = getParentUrlParam('l')
    if (code) {
      setLCode(code)
      fetchLicense(code)
    } else {
      setLoading(false)
      setError('No license code specified. Use ?l=CODE in the parent URL.')
    }
  }, [fetchLicense])

  return {
    loading,
    actionLoading,
    error,
    setError,
    successMsg,
    setSuccessMsg,
    data,
    lCode,
    form,
    formHasChanges,
    updateFormField,
    resetForm,
    postAction,
    parentOrigin,
  }
}
